import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { PracticeRunnerService } from "./practice-runner.service";
import type { Locale } from "../core/gamification/gamification.types";

/**
 * Result modal shown after a Practice run finishes. Reads
 * {@link PracticeRunnerService.lastResult}; the modal mounts in the
 * template only when the result signal is non-null, so visibility is
 * controlled by the parent's `@if` block.
 *
 * No badges section — Practice mode does not unlock badges per the MVP
 * plan. Career mode (Etap 2) will add a separate award panel.
 */
@Component({
  selector: "po-practice-result",
  standalone: true,
  imports: [DecimalPipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./practice-result.component.html",
  styleUrl: "./practice-result.component.css",
})
export class PracticeResultComponent {
  private readonly runner = inject(PracticeRunnerService);
  private readonly translate = inject(TranslateService);

  readonly retry = output<void>();
  readonly changeSettings = output<void>();
  readonly exit = output<void>();

  protected readonly result = this.runner.lastResult;

  protected readonly currentLocale = computed<Locale>(() =>
    coerceLocale(this.translate.currentLang ?? this.translate.getDefaultLang()),
  );

  protected readonly maneuverLabel = computed(() => {
    const r = this.result();
    if (!r) return "";
    return r.declaration.def.labels[this.currentLocale()];
  });

  protected readonly tierKey = computed(
    () => "gamification.tier." + (this.result()?.entry.tier ?? "calm"),
  );

  protected readonly statusKey = computed(
    () =>
      "gamification.practice.result." +
      (this.result()?.entry.status ?? "completed"),
  );

  protected readonly breakdown = computed(
    () => this.result()?.maneuverResult.breakdown ?? null,
  );

  protected onRetry(): void {
    this.runner.dismissResult();
    this.retry.emit();
  }

  protected onChangeSettings(): void {
    this.runner.dismissResult();
    this.changeSettings.emit();
  }

  protected onExit(): void {
    this.runner.dismissResult();
    this.exit.emit();
  }
}

function coerceLocale(lang: string | undefined): Locale {
  return lang === "en" ? "en" : "pl";
}
